/**
 * PTEL Tool - CoordinateFields
 * Campos para entrada y conversión de coordenadas
 * 
 * Funcionalidades:
 * - Campo "Pegar coordenadas" acepta cualquier formato (DMS, DD, UTM)
 * - Detección automática de formato al pegar
 * - Conversión automática a UTM30 (EPSG:25830)
 * - Campos X/Y editables manualmente
 * - Validación visual en tiempo real
 * 
 * @version 1.0.0
 * @task E05
 * @date 2025-12-11
 */

import React, { useState, useCallback, useMemo, useLayoutEffect } from 'react';
import {
  MapPin,
  Clipboard,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Navigation,
} from 'lucide-react';

import type { Theme } from './types';
import { dsColors } from './dsColors';
import { 
  parseCoordinateInput, 
  type ParsedCoordinate,
  type CoordinateFormat,
} from '../../utils/coordinateParser';
import { toUtmInZone, ANDALUCIA_BOUNDS_UTM30, type UtmCoordinate } from '../../utils/projections';
// FMT-03: Pipeline para limpiar texto pegado
import { preProcessCoordinateValue } from '../../pipeline/integration';

// ============================================================================
// TYPES
// ============================================================================

export interface CoordinateFieldsProps {
  /** Tema actual (dark/light) */
  theme: Theme;
  /** Coordenada X actual (UTM30) */
  currentX?: number | null;
  /** Coordenada Y actual (UTM30) */
  currentY?: number | null;
  /** Callback cuando cambian las coordenadas */
  onCoordinatesChange: (x: number | null, y: number | null) => void;
  /** Indica si el formulario está deshabilitado */
  disabled?: boolean;
}

interface ConversionState {
  parsed: ParsedCoordinate | null;
  utm30: UtmCoordinate | null;
  error: string | null;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const FORMAT_LABELS: Record<CoordinateFormat, string> = {
  DMS_GOOGLE: 'DMS Google Maps',
  DMS_WIKIPEDIA: 'DMS Wikipedia',
  DMS_SPANISH: 'DMS Español',
  DMS_SPACES: 'DMS Espacios',
  DD: 'Grados Decimales',
  UTM: 'UTM',
  PLUSCODE: 'PlusCode (OLC)',
  UNKNOWN: 'Desconocido',
};

const FORMAT_COLORS: Record<CoordinateFormat, { dark: string; light: string }> = {
  DMS_GOOGLE: { dark: '#10B981', light: '#059669' },
  DMS_WIKIPEDIA: { dark: '#10B981', light: '#059669' },
  DMS_SPANISH: { dark: '#10B981', light: '#059669' },
  DMS_SPACES: { dark: '#10B981', light: '#059669' },
  DD: { dark: '#3B82F6', light: '#2563EB' },
  UTM: { dark: '#8B5CF6', light: '#7C3AED' },
  PLUSCODE: { dark: '#F59E0B', light: '#D97706' },
  UNKNOWN: { dark: '#6B7280', light: '#4B5563' },
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convierte ParsedCoordinate (WGS84) a UTM30
 */
function convertToUtm30(parsed: ParsedCoordinate): UtmCoordinate | null {
  try {
    // Siempre convertir a UTM30 (EPSG:25830) para consistencia
    return toUtmInZone(parsed.lat, parsed.lon, 'EPSG:25830');
  } catch {
    return null;
  }
}

/**
 * Valida si X/Y son válidos para Andalucía UTM30
 */
function isValidUtm30Andalucia(x: number, y: number): boolean {
  // OBS-ARCH-UTM30-BOUNDS-DUP (FORK-A A.2 08-jul): bebe de ANDALUCIA_BOUNDS_UTM30 (fuente
  // única) en vez de replicar los rangos a mano — mismo anti-patrón que G1 corrigió en
  // isValidUTMValue. Rangos idénticos (100000/620000/3975000/4290000): sin cambio funcional.
  return (
    x >= ANDALUCIA_BOUNDS_UTM30.minX && x <= ANDALUCIA_BOUNDS_UTM30.maxX &&
    y >= ANDALUCIA_BOUNDS_UTM30.minY && y <= ANDALUCIA_BOUNDS_UTM30.maxY
  );
}

/**
 * Formatea número con decimales limitados
 */
function formatCoord(value: number, decimals: number = 2): string {
  return value.toFixed(decimals);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const CoordinateFields: React.FC<CoordinateFieldsProps> = ({
  theme,
  currentX,
  currentY,
  onCoordinatesChange,
  disabled = false,
}) => {
  const t = dsColors;
  
  // Estado del campo de entrada
  const [inputText, setInputText] = useState('');
  
  // Estado de conversión
  const [conversion, setConversion] = useState<ConversionState>({
    parsed: null,
    utm30: null,
    error: null,
  });
  
  // Campos X/Y manuales
  const [manualX, setManualX] = useState<string>(currentX?.toString() || '');
  const [manualY, setManualY] = useState<string>(currentY?.toString() || '');
  const [manualMode, setManualMode] = useState(false);
  
  // Sincronizar campos manuales con props
  // ACT.01: useLayoutEffect (síncrono) evita que setManualX/Y escapen de act() en tests
  useLayoutEffect(() => {
    if (!manualMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sincronización props→estado; useLayoutEffect síncrono, no causa cascada
      setManualX(currentX?.toFixed(2) || '');
       
      setManualY(currentY?.toFixed(2) || '');
    }
  }, [currentX, currentY, manualMode]);
  
  // Procesar texto pegado
  // FMT-03: Estado para mostrar si se aplicó limpieza del pipeline
  const [pipelineApplied, setPipelineApplied] = useState(false);
  
  const handleInputChange = useCallback((text: string) => {
    setInputText(text);
    setManualMode(false);
    setPipelineApplied(false);
    
    if (!text.trim()) {
      setConversion({ parsed: null, utm30: null, error: null });
      return;
    }
    
    // FMT-03: Aplicar pipeline para limpiar mojibake y artefactos de copia/pega
    const preProcessed = preProcessCoordinateValue(text, 'plain');
    const cleanedText = preProcessed.cleaned;
    const wasModified = preProcessed.modifications > 0;
    
    if (wasModified) {
      setPipelineApplied(true);
    }
    
    const parsed = parseCoordinateInput(cleanedText);
    
    if (!parsed) {
      setConversion({
        parsed: null,
        utm30: null,
        error: 'No se reconoce el formato de coordenadas',
      });
      return;
    }
    
    const utm30 = convertToUtm30(parsed);
    
    if (!utm30) {
      setConversion({
        parsed,
        utm30: null,
        error: 'Error al convertir a UTM30',
      });
      return;
    }
    
    setConversion({ parsed, utm30, error: null });
    setManualX(utm30.x.toFixed(2));
    setManualY(utm30.y.toFixed(2));
  }, []);
  
  // Validación de campos manuales
  const manualValidation = useMemo(() => {
    const x = parseFloat(manualX);
    const y = parseFloat(manualY);
    
    if (!manualX && !manualY) {
      return { valid: true, message: '' };
    }
    
    if (isNaN(x) || isNaN(y)) {
      return { valid: false, message: 'Valores numéricos requeridos' };
    }
    
    if (!isValidUtm30Andalucia(x, y)) {
      return { valid: false, message: 'Coordenadas fuera del rango de Andalucía' };
    }
    
    return { valid: true, message: '' };
  }, [manualX, manualY]);
  
  // Aplicar coordenadas
  const handleApply = useCallback(() => {
    const x = parseFloat(manualX);
    const y = parseFloat(manualY);
    
    if (manualValidation.valid && !isNaN(x) && !isNaN(y)) {
      onCoordinatesChange(x, y);
    }
  }, [manualX, manualY, manualValidation.valid, onCoordinatesChange]);
  
  // Manejar cambios manuales en X/Y
  const handleManualXChange = useCallback((value: string) => {
    setManualMode(true);
    setManualX(value);
  }, []);
  
  const handleManualYChange = useCallback((value: string) => {
    setManualMode(true);
    setManualY(value);
  }, []);
  
  // Determinar estado visual
  const hasResult = conversion.parsed !== null && conversion.utm30 !== null;
  const hasError = conversion.error !== null;
  const confidence = conversion.parsed?.confidence ?? 0;
  
  return (
    <div className="space-y-3">
      {/* Campo de entrada principal */}
      <div className="space-y-1.5">
        <label 
          className="flex items-center gap-2 text-xs font-medium" 
          style={{ color: t.textMuted }}
        >
          <Clipboard size={16} style={{ color: t.iconMuted }} />
          <span>Pegar coordenadas</span>
          <span className="text-[10px]" style={{ color: t.textDisabled }}>
            (DMS, DD o UTM)
          </span>
        </label>
        
        <input
          type="text"
          value={inputText}
          onChange={e => handleInputChange(e.target.value)}
          placeholder="Ej: 37°11'03.5&quot;N 3°36'21.5&quot;W"
          disabled={disabled}
          className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-all"
          style={{
            backgroundColor: t.bgMuted,
            border: `2px solid ${
              hasError ? t.error : 
              hasResult ? t.success : 
              t.border
            }`,
            color: t.text,
            opacity: disabled ? 0.5 : 1,
          }}
        />
        
        {/* Mensaje de error */}
        {hasError && (
          <p className="text-xs flex items-center gap-1" style={{ color: t.error }}>
            <XCircle size={12} />
            {conversion.error}
          </p>
        )}
      </div>
      
      {/* Resultado de conversión */}
      {hasResult && conversion.parsed && conversion.utm30 && (
        <div
          className="p-3 rounded-lg space-y-2"
          style={{ 
            backgroundColor: `${t.success}10`,
            border: `1px solid ${t.success}40`,
          }}
        >
          {/* Badge de formato */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span
                className="px-2 py-0.5 text-[10px] font-medium rounded-full"
                style={{
                  backgroundColor: FORMAT_COLORS[conversion.parsed.format][theme],
                  color: '#FFFFFF',
                }}
              >
                {FORMAT_LABELS[conversion.parsed.format]}
              </span>
              {/* FMT-03: Indicador de limpieza del pipeline */}
              {pipelineApplied && (
                <span
                  className="px-1.5 py-0.5 text-[9px] font-medium rounded"
                  style={{
                    backgroundColor: `${t.info}20`,
                    color: t.info,
                  }}
                  title="El texto fue limpiado automáticamente"
                >
                  ✨ Limpiado
                </span>
              )}
            </div>
            <span 
              className="text-[10px]"
              style={{ color: confidence >= 70 ? t.success : t.warning }}
            >
              Confianza: {confidence}%
            </span>
          </div>
          
          {/* Coordenadas WGS84 */}
          <div className="flex gap-4 text-xs">
            <div>
              <span style={{ color: t.textMuted }}>Lat: </span>
              <span style={{ color: t.text }}>
                {formatCoord(conversion.parsed.lat, 6)}°
              </span>
            </div>
            <div>
              <span style={{ color: t.textMuted }}>Lon: </span>
              <span style={{ color: t.text }}>
                {formatCoord(conversion.parsed.lon, 6)}°
              </span>
            </div>
          </div>
          
          {/* Resultado UTM30 */}
          <div 
            className="flex items-center gap-2 pt-1"
            style={{ borderTop: `1px solid ${t.border}` }}
          >
            <Navigation size={12} style={{ color: t.accent }} />
            <span className="text-xs font-medium" style={{ color: t.text }}>
              UTM30: X={formatCoord(conversion.utm30.x, 2)} Y={formatCoord(conversion.utm30.y, 2)}
            </span>
          </div>
          
          {/* Warnings */}
          {conversion.parsed.warnings && conversion.parsed.warnings.length > 0 && (
            <div className="space-y-1">
              {conversion.parsed.warnings.map((warning, i) => (
                <p 
                  key={i}
                  className="text-[10px] flex items-center gap-1"
                  style={{ color: t.warning }}
                >
                  <AlertTriangle size={10} />
                  {warning}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Campos X/Y manuales */}
      <div className="space-y-1.5">
        <label 
          className="flex items-center gap-2 text-xs font-medium" 
          style={{ color: t.textMuted }}
        >
          <MapPin size={16} style={{ color: t.iconMuted }} />
          <span>Coordenadas UTM30</span>
          {manualMode && (
            <span 
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ backgroundColor: `${t.accent}20`, color: t.accent }}
            >
              Edición manual
            </span>
          )}
        </label>
        
        <div className="grid grid-cols-2 gap-2">
          {/* Campo X */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[10px] font-medium" style={{ color: t.textDisabled }}>
                X (Este)
              </span>
            </div>
            <input
              type="text"
              value={manualX}
              onChange={e => handleManualXChange(e.target.value)}
              placeholder="447850.00"
              disabled={disabled}
              className="w-full px-2 py-1.5 text-sm rounded outline-none transition-all font-mono"
              style={{
                backgroundColor: t.bgMuted,
                border: `1px solid ${
                  manualValidation.valid ? t.border : t.error
                }`,
                color: t.text,
                opacity: disabled ? 0.5 : 1,
              }}
            />
          </div>
          
          {/* Campo Y */}
          <div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[10px] font-medium" style={{ color: t.textDisabled }}>
                Y (Norte)
              </span>
            </div>
            <input
              type="text"
              value={manualY}
              onChange={e => handleManualYChange(e.target.value)}
              placeholder="4116234.00"
              disabled={disabled}
              className="w-full px-2 py-1.5 text-sm rounded outline-none transition-all font-mono"
              style={{
                backgroundColor: t.bgMuted,
                border: `1px solid ${
                  manualValidation.valid ? t.border : t.error
                }`,
                color: t.text,
                opacity: disabled ? 0.5 : 1,
              }}
            />
          </div>
        </div>
        
        {/* Error de validación manual */}
        {!manualValidation.valid && (
          <p className="text-xs flex items-center gap-1" style={{ color: t.error }}>
            <AlertTriangle size={12} />
            {manualValidation.message}
          </p>
        )}
      </div>
      
      {/* Botón aplicar */}
      {(hasResult || manualMode) && manualValidation.valid && (
        <button
          onClick={handleApply}
          disabled={disabled}
          className="w-full px-3 py-2 text-sm font-medium rounded-lg flex items-center justify-center gap-2 transition-colors"
          style={{
            backgroundColor: t.accent,
            color: theme === 'dark' ? '#171717' : '#FFFFFF',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <CheckCircle2 size={16} />
          Aplicar coordenadas
        </button>
      )}
      
      {/* Info helper */}
      {!inputText && !currentX && !currentY && (
        <div
          className="flex items-start gap-2 p-2 rounded text-[10px]"
          style={{ backgroundColor: `${t.info}10`, color: t.textMuted }}
        >
          <Info size={12} className="shrink-0 mt-0.5" style={{ color: t.info }} />
          <div>
            <p className="font-medium" style={{ color: t.text }}>Formatos aceptados:</p>
            <ul className="mt-1 space-y-0.5">
              <li>• DMS: 37°11'03.5"N 3°36'21.5"W</li>
              <li>• DD: 37.184306, -3.605972</li>
              <li>• UTM: 447850.23, 4116234.56</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoordinateFields;
